import { Box, Button, Tab, Tabs } from '@mui/material';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import logo from "../assets/images/logo.png";
import useAuth from '../hooks/useAuth';
import CertificateDialog from '../views/pages/Dashboard/student/CertificateDialog';
import CustomTextField from './Textfield';

function VerifyCertificate() {
  const [activeTab, setActiveTab] = useState(0);

  // Student verification state
  const [enrollmentNo, setEnrollmentNo] = useState(null);
  const [certificateData, setCertificateData] = useState(null);
  const [isLoadingStudent, setIsLoadingStudent] = useState(false);

  // Employee verification state
  const [employeeCertificateUrl, setEmployeeCertificateUrl] = useState(null);
  const [isLoadingEmployee, setIsLoadingEmployee] = useState(false);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);

  const { request } = useAuth();

  const {
    register: registerStudent,
    handleSubmit: handleSubmitStudent,
    formState: { errors: errorsStudent },
    reset: resetStudent,
  } = useForm();

  const {
    register: registerEmployee,
    handleSubmit: handleSubmitEmployee,
    formState: { errors: errorsEmployee },
    reset: resetEmployee,
  } = useForm();

  const handleVerifyStudent = async (values) => {
    setIsLoadingStudent(true);
    setCertificateData(null);
    try {
      const res = await request.get('/student/verify/student', {
        params: { enrollment_no: values.enrollment_no },
      });
      setCertificateData(res.data.url);
      setEnrollmentNo(values.enrollment_no);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong');
    } finally {
      setIsLoadingStudent(false);
    }
  };

  const handleCloseStudentDialog = () => {
    setEnrollmentNo(null);
    setCertificateData(null);
    resetStudent();
  };

  const handleVerifyEmployee = async (values) => {
    setIsLoadingEmployee(true);
    setEmployeeCertificateUrl(null);
    try {
      const res = await request.get('/employee/verify/employee', {
        params: { certificate_no: values.certificate_no },
      });
      setEmployeeCertificateUrl(res.data.url);
      setShowEmployeeDialog(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong');
    } finally {
      setIsLoadingEmployee(false);
    }
  };

  const handleCloseEmployeeDialog = () => {
    setShowEmployeeDialog(false);
    setEmployeeCertificateUrl(null);
    resetEmployee();
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <img src={logo} alt="Logo" style={styles.logo} />
        <h1 style={styles.heading}>Verify Your Certificate</h1>
        <p style={styles.subtitle}>Select a category and enter your certificate number</p>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': { fontSize: 14, fontWeight: 600 },
              '& .Mui-selected': { color: '#454751d1 !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#454751d1' },
            }}
          >
            <Tab label="Student" />
            <Tab label="Employee" />
          </Tabs>
        </Box>

        {/* Student Verification */}
        {activeTab === 0 && (
          <form onSubmit={handleSubmitStudent(handleVerifyStudent)}>
            <CustomTextField
              {...registerStudent('enrollment_no', { required: 'Enrollment number is required' })}
              fullWidth
              label="Certificate / Enrollment Number*"
              error={!!errorsStudent.enrollment_no}
              helperText={errorsStudent.enrollment_no?.message}
            />

            {isLoadingStudent && (
              <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <span style={styles.loadingText}>Verifying certificate...</span>
              </div>
            )}

            <Button
              sx={{ fontSize: 14, background: '#454751d1', mt: 2 }}
              style={{
                ...styles.button,
                opacity: isLoadingStudent ? 0.6 : 1,
                cursor: isLoadingStudent ? 'not-allowed' : 'pointer',
              }}
              type="submit"
              variant="contained"
              disabled={isLoadingStudent}
            >
              {isLoadingStudent ? 'Verifying...' : 'Verify'}
            </Button>
          </form>
        )}

        {/* Employee Verification */}
        {activeTab === 1 && (
          <form onSubmit={handleSubmitEmployee(handleVerifyEmployee)}>
            <CustomTextField
              {...registerEmployee('certificate_no', { required: 'Certificate number is required' })}
              fullWidth
              label="Certificate Number*"
              error={!!errorsEmployee.certificate_no}
              helperText={errorsEmployee.certificate_no?.message}
            />

            {isLoadingEmployee && (
              <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <span style={styles.loadingText}>Verifying certificate...</span>
              </div>
            )}

            <Button
              sx={{ fontSize: 14, background: '#454751d1', mt: 2 }}
              style={{
                ...styles.button,
                opacity: isLoadingEmployee ? 0.6 : 1,
                cursor: isLoadingEmployee ? 'not-allowed' : 'pointer',
              }}
              type="submit"
              variant="contained"
              disabled={isLoadingEmployee}
            >
              {isLoadingEmployee ? 'Verifying...' : 'Verify'}
            </Button>
          </form>
        )}
      </div>

      {/* Student Certificate Dialog */}
      <CertificateDialog
        open={!!certificateData}
        onClose={handleCloseStudentDialog}
        verify={true}
        studentId={enrollmentNo}
        isEnrollmentBased={true}
        certificateUrl={certificateData}
        skipApiCall={true}
      />

      {/* Employee Certificate Dialog */}
      <CertificateDialog
        open={showEmployeeDialog}
        onClose={handleCloseEmployeeDialog}
        verify={true}
        certificateUrl={employeeCertificateUrl}
        skipApiCall={true}
      />
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    background: 'linear-gradient(to right, #ece9e6, #ffffff)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '10px',
  },
  card: {
    backgroundColor: '#ffffff',
    padding: '20px 30px',
    borderRadius: '16px',
    boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
    width: '100%',
    maxWidth: '400px',
  },
  logo: {
    width: '120px',
  },
  heading: {
    fontSize: '26px',
    fontWeight: '700',
    color: '#333',
    marginTop: -5,
  },
  subtitle: {
    fontSize: '14px',
    color: '#777',
    marginBottom: '20px',
  },
  button: {
    backgroundColor: 'rgb(59 55 65)',
    color: '#fff',
    border: 'none',
    padding: '8px',
    width: '100%',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background 0.3s',
    marginBottom: 10,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '15px',
    marginTop: '10px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid #f3f3f3',
    borderTop: '2px solid #333',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: '8px',
  },
  loadingText: {
    fontSize: '14px',
    color: '#666',
  },
};

export default VerifyCertificate;
